import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CSSTransition } from 'react-transition-group';
import { H3 } from '../Heading';
import { IconCancel } from '../Icon';
import { Portal, PortalNamespace } from '../Portal';
import { SrOnly } from '../SrOnly';
import {
  animationDuration,
  Body,
  Close,
  CloseBtn,
  Content,
  Dialog,
  Footer,
  Header,
  Heading,
  UiBlock,
  Wrapper,
} from './wrapper';

interface ModalProps {
  id?: string | null;
  title: string | null;
  children: React.ReactNode;
  onClose: () => void;
  size?: 'narrow' | 'small' | 'wide' | 'extraWide';
  chat?: boolean;
  footer?: React.ReactNode;
}

const Modal = ({ id, title, onClose, children, size, chat, footer }: ModalProps) => {
  const { t } = useTranslation('common');
  const [animate, setAnimate] = useState(true);
  const nodeRef = useRef<HTMLDivElement | null>(null);

  const onCloseAnimate = useCallback(() => {
    setAnimate(false);
    setTimeout(() => onClose(), animationDuration);
  }, []);

  // Close on escape key press
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCloseAnimate();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onCloseAnimate]);

  return (
    <Portal id={id || PortalNamespace.DefaultPortal}>
      <CSSTransition
        nodeRef={nodeRef}
        in={animate}
        appear={true}
        timeout={animationDuration}
        classNames="modal"
      >
        <Wrapper ref={nodeRef} $chat={chat} $size={size}>
          <UiBlock onClick={onCloseAnimate} title={t('closeModal', { ns: 'common' }) || ''} />
          <Dialog>
            <Content>
              <Header>
                <Heading>
                  <H3 as="h2" $margin="none">
                    {title}
                  </H3>
                </Heading>
                <Close>
                  <CloseBtn
                    onClick={onCloseAnimate}
                    title={t('closeModal', { ns: 'common' }) || ''}
                  >
                    <SrOnly>{t('closeModal', { ns: 'common' })}</SrOnly>
                    <IconCancel />
                  </CloseBtn>
                </Close>
              </Header>
              <Body>{children}</Body>
              {footer && <Footer>{footer}</Footer>}
            </Content>
          </Dialog>
        </Wrapper>
      </CSSTransition>
    </Portal>
  );
};

export default Modal;
